import { FC, ReactElement, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { PayrollPeriod } from '../../../@paco/entities/PayrollPeriod/PayrollPeriod'
import { getSettingEnabled } from '../../../@paco/entities/Setting/SettingHelpers'
import { Track, TrackFormData } from '../../../@paco/entities/Track/Track'
import { useTypedSelector } from '../../../@paco/redux/store'
import { setToast } from '../../../@paco/redux/toasts/toastsReducer'
import { approveTrack, editTrack } from '../../../@paco/redux/tracks/tracksActions'
import { setError } from '../../../@paco/redux/tracks/tracksReducer'
import { ToastType } from '../../../@paco/types'

import { CompletePeriodTracksToReviewContainer } from '../../../containers'
import { Reducers } from '../../../redux/reducers'
import { useTypedDispatch } from '../../../redux/store'

interface ConnectedCompletePeriodTracksToReviewProps {
    activePeriod?: PayrollPeriod;
    className?: string;
}

const ConnectedCompletePeriodTracksToReview: FC<ConnectedCompletePeriodTracksToReviewProps> = ({ activePeriod, className }): ReactElement => {
    const dispatch = useTypedDispatch()

    const { permissions, role: currentUserRole } = useTypedSelector(state => state.authenticatedUserReducer)
    const { payrollPeriods } = useTypedSelector(state => state.pacoPayrollPeriodsReducer)
    const { settings } = useTypedSelector(state => state.pacoSettingsReducer)
    const isTimerEnableFeedback = getSettingEnabled('TIMER_ENABLE_FEEDBACK', settings)
    const isLoading = useSelector<Reducers, boolean>(state => state.tracksToReviewReducer.isLoading)
    const {
        isLoading: isTracksLoading,
        error: tracksError,
        loadingTrackIds,
        tracksToReview,
    } = useTypedSelector(state => state.pacoTracksReducer)

    const handleSubmitTrack = async (
        trackId: string,
        departmentId: string,
        formData: TrackFormData,
        approved?: boolean,
        endedOpenTrackFromManager?: boolean,
    ): Promise<void> => {
        if (activePeriod) {
            await dispatch(editTrack(trackId, departmentId, formData, undefined, approved, endedOpenTrackFromManager))
        }
    }

    const handleAcceptTrack = async (track: Track): Promise<void> => {
        if (activePeriod) {
            await dispatch(approveTrack(track.id))
        }
    }

    useEffect(() => {
        if (tracksError) {
            dispatch(setToast({
                text: tracksError,
                type: ToastType.fail,
                shouldFadeOutAutomatically: false,
            }))
            dispatch(setError(''))
        }
    }, [tracksError])

    return (
        <CompletePeriodTracksToReviewContainer
            isLoading={isLoading || isTracksLoading}
            currentUserRole={currentUserRole}
            isTimerEnableFeedback={isTimerEnableFeedback}
            payrollPeriods={payrollPeriods}
            permissions={permissions}
            settings={settings}
            tracksToReview={tracksToReview}
            loadingTrackIds={loadingTrackIds}
            onSubmitTrack={handleSubmitTrack}
            onAcceptTrack={handleAcceptTrack}
            className={className}
        />
    )
}

export default ConnectedCompletePeriodTracksToReview
